import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useCreateAdContext } from '../../context/CreateAdContext';
import { getRequest, postRequest } from '../../services/api/apiService';
import LibraryPickerCard from './LibraryPickerCard';

const FIELD_LABELS = {
  name: 'Campaign',
  publisherPlatform: 'Platform',
  objective: 'Objective',
  status: 'Status',
  location: 'Location',
  gender: 'Gender',
  age: 'Age',
  languages: 'Language',
  adSetName: 'Ad Set',
  adCreativeName: 'Creative Name',
  creativeType: 'Format',
  adTitle: 'Title',
  adText: 'Body',
  description: 'Description',
  callToAction: 'CTA',
  destinationUrl: 'URL',
  budgetMeta: 'Meta Budget',
  budgetTikTok: 'TikTok Budget',
  budgetGoogle: 'Google Budget',
  budgetSnapchat: 'Snapchat Budget',
  budgetLinkedIn: 'LinkedIn Budget',
  startTime: 'Start Date',
  endTime: 'End Date',
};

const formatActionValue = (field, value) => {
  if (Array.isArray(value)) return value.join(', ');
  if (field === 'age' && Array.isArray(value)) return `${value[0]}–${value[value.length - 1]}`;
  if (typeof value === 'number') return `${value} €`;
  if (typeof value === 'string' && value.includes('T')) return value.split('T')[0];
  return String(value);
};

const McpResultCard = ({ tool, data }) => {
  if (!data) return null;

  if (tool === 'meta_list_campaigns') {
    const items = Array.isArray(data) ? data : [];
    if (items.length === 0) return <p className="text-xs text-gray-500 italic">No campaigns found.</p>;
    return (
      <div className="mt-1 rounded-xl border border-gray-200 overflow-hidden text-xs">
        <table className="w-full">
          <thead className="bg-[#221C51] text-white">
            <tr>
              <th className="px-2 py-1.5 text-left font-medium">Name</th>
              <th className="px-2 py-1.5 text-left font-medium">Status</th>
              <th className="px-2 py-1.5 text-left font-medium">Budget/day</th>
            </tr>
          </thead>
          <tbody>
            {items.map((c, i) => (
              <tr key={c.id || i} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                <td className="px-2 py-1.5 max-w-[110px] truncate font-medium text-[#221C51]">{c.name}</td>
                <td className="px-2 py-1.5">
                  <span className={`px-1.5 py-0.5 rounded-full font-semibold ${
                    c.status === 'ACTIVE' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
                  }`}>{c.status}</span>
                </td>
                <td className="px-2 py-1.5 text-gray-600">
                  {c.daily_budget ? `${(c.daily_budget / 100).toFixed(0)} €` : '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  if (tool === 'meta_get_insights') {
    const items = Array.isArray(data) ? data : [];
    if (items.length === 0) return <p className="text-xs text-gray-500 italic">No insights data.</p>;
    return (
      <div className="mt-1 rounded-xl border border-gray-200 overflow-hidden text-xs">
        <table className="w-full">
          <thead className="bg-[#221C51] text-white">
            <tr>
              <th className="px-2 py-1.5 text-left font-medium">Campaign</th>
              <th className="px-2 py-1.5 text-right font-medium">Spend</th>
              <th className="px-2 py-1.5 text-right font-medium">Impressions</th>
              <th className="px-2 py-1.5 text-right font-medium">CTR</th>
            </tr>
          </thead>
          <tbody>
            {items.map((r, i) => (
              <tr key={r.campaignId || i} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                <td className="px-2 py-1.5 max-w-[100px] truncate font-medium text-[#221C51]">{r.campaignName || r.campaignId}</td>
                <td className="px-2 py-1.5 text-right">{r.spend} €</td>
                <td className="px-2 py-1.5 text-right">{Number(r.impressions).toLocaleString()}</td>
                <td className="px-2 py-1.5 text-right">{r.ctr}%</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  if (tool === 'meta_list_adsets') {
    const items = Array.isArray(data) ? data : [];
    if (items.length === 0) return <p className="text-xs text-gray-500 italic">No ad sets found.</p>;
    return (
      <div className="mt-1 rounded-xl border border-gray-200 overflow-hidden text-xs">
        <table className="w-full">
          <thead className="bg-[#221C51] text-white">
            <tr>
              <th className="px-2 py-1.5 text-left font-medium">Ad Set</th>
              <th className="px-2 py-1.5 text-left font-medium">Status</th>
              <th className="px-2 py-1.5 text-left font-medium">Goal</th>
            </tr>
          </thead>
          <tbody>
            {items.map((s, i) => (
              <tr key={s.id || i} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                <td className="px-2 py-1.5 font-medium text-[#221C51] truncate max-w-[110px]">{s.name}</td>
                <td className="px-2 py-1.5">
                  <span className={`px-1.5 py-0.5 rounded-full font-semibold ${
                    s.status === 'ACTIVE' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
                  }`}>{s.status}</span>
                </td>
                <td className="px-2 py-1.5 text-gray-600">{s.optimization_goal || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  if (tool === 'meta_search_interests') {
    const items = Array.isArray(data) ? data : [];
    if (items.length === 0) return <p className="text-xs text-gray-500 italic">No interests found.</p>;
    return (
      <div className="mt-1 flex flex-wrap gap-1">
        {items.slice(0, 12).map((i) => (
          <span key={i.id} className="bg-purple-50 text-[#221C51] border border-purple-200 rounded-full px-2 py-0.5 text-xs font-medium">
            {i.name}
          </span>
        ))}
      </div>
    );
  }

  if (data?.success !== undefined || data?.id) {
    return (
      <div className="mt-1 bg-green-50 border border-green-200 rounded-xl px-3 py-2 text-xs text-green-700 font-medium">
        ✓ Action applied successfully
      </div>
    );
  }

  return (
    <pre className="mt-1 text-xs bg-gray-50 border border-gray-200 rounded-xl p-2 overflow-auto max-h-32">
      {JSON.stringify(data, null, 2)}
    </pre>
  );
};

const TypingDots = () => (
  <div className="flex items-center gap-1 px-1 py-0.5">
    {[0, 150, 300].map((delay) => (
      <span
        key={delay}
        className="w-2 h-2 bg-[#221C51] rounded-full animate-bounce"
        style={{ animationDelay: `${delay}ms` }}
      />
    ))}
  </div>
);

const WELCOME_MESSAGE = {
  role: 'assistant',
  content: "Hello! I'm your OneBotAds AI assistant.\n\nDescribe your campaign in a few words and I'll set everything up for you.",
  actions: [],
};

const QUICK_CHIPS = [
  { label: '🚀 Create a Meta campaign',  text: 'I want to create a Meta campaign' },
  { label: '🎵 Set up a TikTok ad',       text: 'Help me set up a TikTok ad' },
  { label: '🎨 Generate an ad creative',  text: 'Generate an ad creative for me' },
  { label: '📚 Use a pic from my library', text: 'Show me my library so I can pick a creative' },
  { label: '💰 Help me set my budget',    text: 'Help me choose the right budget' },
];

// Client-side cap on the chat input (BUG-011 / CT-077) — keeps a single message
// well within the LLM's usable context alongside the system prompt + history.
const MAX_CHAT_LENGTH = 2000;

const AiChatPanel = ({ shopifyProduct = null }) => {
  const productWelcome = shopifyProduct
    ? {
        role: 'assistant',
        content: `Hello! I'm your OneBotAds AI assistant.\n\nI detected your Shopify product: **${shopifyProduct.title}**${shopifyProduct.price ? ` (${shopifyProduct.price})` : ''}.\n\nThe form is pre-filled. Tell me which platform you want to run on and I'll configure the rest!`,
        actions: [],
      }
    : WELCOME_MESSAGE;

  const [messages, setMessages] = useState([productWelcome]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [confirmSubmit, setConfirmSubmit] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const { dispatchActions, requestSubmit } = useCreateAdContext();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, loading]);

  useEffect(() => {
    setTimeout(() => inputRef.current?.focus(), 100);
  }, []);

  const handleMcpCall = useCallback(async ({ tool, args }) => {
    const placeholderId = Date.now();
    setMessages((prev) => [...prev, {
      role: 'assistant',
      content: `Calling Meta API: ${tool}…`,
      actions: [],
      generating: true,
      id: placeholderId,
    }]);

    try {
      const res = await postRequest('/api/mcp/meta/tool', { tool, args: args || {} });
      const resultData = res?.result;
      setMessages((prev) => prev.map((m) =>
        m.id === placeholderId
          ? { ...m, content: `Meta API — ${tool.replace('meta_', '').replace(/_/g, ' ')}:`, generating: false, mcpTool: tool, mcpData: resultData }
          : m
      ));
    } catch (err) {
      const errText = err?.response?.data?.error || err.message;
      setMessages((prev) => prev.map((m) =>
        m.id === placeholderId
          ? { ...m, content: `❌ Meta API error: ${errText}`, generating: false }
          : m
      ));
    }
  }, []);

  const handleGenerateCreative = useCallback(async (type, { prompt, aspectRatio, engine, quality }) => {
    const isImage = type === 'image';
    const field = isImage ? 'imageUrl' : 'videoUrl';
    const engineLabel = engine && engine !== 'auto' ? ` (${engine.charAt(0).toUpperCase() + engine.slice(1)})` : '';
    const label = isImage ? `🖼️ Image${engineLabel}` : `🎬 Video${engineLabel}`;

    const placeholderId = Date.now();
    setMessages((prev) => [...prev, {
      role: 'assistant',
      content: `${label} generating…`,
      actions: [],
      generating: true,
      id: placeholderId,
    }]);

    const isOnCreateAd = location.pathname.includes('create-ad');
    if (!isOnCreateAd) navigate('/dashboard/create-ad-ai');

    try {
      const res = await postRequest('/api/creative/generate', {
        type, prompt, aspectRatio, engine: engine || 'auto', quality: quality || 'standard', save_to_library: true,
      });
      if (!res?.url) throw new Error('Missing URL in response');
      dispatchActions([{ field, value: res.url }]);
      setMessages((prev) => prev.map((m) =>
        m.id === placeholderId
          ? { ...m, content: `${label} generated and added to the form ✅`, generating: false, generatedUrl: isImage ? res.url : null }
          : m
      ));
    } catch (err) {
      setMessages((prev) => prev.map((m) =>
        m.id === placeholderId
          ? { ...m, content: `❌ Generation failed: ${err?.response?.data?.message || err.message}`, generating: false }
          : m
      ));
    }
  }, [location.pathname, navigate, dispatchActions]);

  // Apply a library asset to the create-ad form (from grid click or _library_pick)
  const handlePickLibraryAsset = useCallback((asset) => {
    const isImage = asset.type === 'image';
    dispatchActions([
      { field: isImage ? 'imageUrl' : 'videoUrl', value: asset.url },
      { field: 'creativeType', value: isImage ? 'Image' : 'Video' },
    ]);

    const label = asset.name || (asset.prompt || '').substring(0, 50) || `#${asset.id}`;
    setMessages((prev) => [...prev, {
      role: 'assistant',
      content: `✅ ${isImage ? 'Image' : 'Video'} "${label}" added to the form.`,
      actions: [],
      generatedUrl: isImage ? asset.url : null,
    }]);
  }, [dispatchActions]);

  const handleLibraryList = useCallback(async ({ type } = {}) => {
    const placeholderId = Date.now();
    setMessages((prev) => [...prev, {
      role: 'assistant',
      content: '📚 Loading your library…',
      actions: [],
      generating: true,
      id: placeholderId,
    }]);

    try {
      const params = { limit: 12 };
      if (type === 'image' || type === 'video') params.type = type;
      const res = await getRequest('/api/ai-ad/library', params);
      const assets = res?.assets || [];

      // Content carries the asset ids so the LLM can _library_pick on follow-up
      const summary = assets.length === 0
        ? 'Your library is empty. Want me to generate an image or video with AI?'
        : `Here is your library — click an asset to use it:\n${assets
            .map((a) => `#${a.id} · ${a.type} · ${a.name || (a.prompt || '').substring(0, 50) || 'untitled'}`)
            .join('\n')}`;

      setMessages((prev) => prev.map((m) =>
        m.id === placeholderId
          ? { ...m, content: summary, generating: false, libraryAssets: assets }
          : m
      ));
    } catch (err) {
      const errText = err?.response?.data?.message || err.message;
      setMessages((prev) => prev.map((m) =>
        m.id === placeholderId
          ? { ...m, content: `❌ Could not load your library: ${errText}`, generating: false }
          : m
      ));
    }
  }, []);

  const handleLibraryPick = useCallback(async ({ id }) => {
    try {
      const res = await getRequest(`/api/ai-ad/library/${id}`);
      if (!res?.asset) throw new Error('Asset not found');
      handlePickLibraryAsset(res.asset);
    } catch {
      setMessages((prev) => [...prev, {
        role: 'assistant',
        content: `❌ Asset #${id} not found in your library. Say "show my library" to see your assets.`,
        actions: [],
      }]);
    }
  }, [handlePickLibraryAsset]);

  const applyActions = useCallback((actions) => {
    if (!actions || actions.length === 0) return;

    const submitAction = actions.find((a) => a.field === '_submit');
    const generateImage = actions.find((a) => a.field === '_generate_image');
    const generateVideo = actions.find((a) => a.field === '_generate_video');
    const mcpCallAction = actions.find((a) => a.field === '_mcp_call');
    const libraryListAction = actions.find((a) => a.field === '_library_list');
    const libraryPickAction = actions.find((a) => a.field === '_library_pick');
    const formActions = actions.filter(
      (a) => !['_submit', '_generate_image', '_generate_video', '_mcp_call', '_library_list', '_library_pick'].includes(a.field)
    );

    if (formActions.length > 0) {
      dispatchActions(formActions);
    }

    if (generateImage) {
      const { prompt, aspectRatio = '1:1', engine = 'higgsfield', quality = 'standard' } = generateImage.value || {};
      if (prompt) handleGenerateCreative('image', { prompt, aspectRatio, engine, quality });
    }

    if (generateVideo) {
      const { prompt, aspectRatio = '9:16', engine = 'kling', quality = 'standard' } = generateVideo.value || {};
      if (prompt) handleGenerateCreative('video', { prompt, aspectRatio, engine, quality });
    }

    if (mcpCallAction?.value?.tool) {
      handleMcpCall(mcpCallAction.value);
    }

    if (libraryListAction) {
      handleLibraryList(libraryListAction.value || {});
    }

    if (libraryPickAction?.value?.id) {
      handleLibraryPick(libraryPickAction.value);
    }

    if (submitAction) {
      setConfirmSubmit(true);
    }
  }, [dispatchActions, handleGenerateCreative, handleMcpCall, handleLibraryList, handleLibraryPick]);

  const sendMessage = useCallback(async () => {
    const text = input.trim();
    if (!text || loading) return;

    const userMsg = { role: 'user', content: text, actions: [] };
    const updatedMessages = [...messages, userMsg];
    setMessages(updatedMessages);
    setInput('');
    setLoading(true);

    try {
      const payload = updatedMessages.map(({ role, content }) => ({ role, content }));
      const res = await postRequest('/api/chat/message', { messages: payload, shopifyProduct });
      const botMsg = { role: 'assistant', content: res.message, actions: res.actions || [] };
      setMessages((prev) => [...prev, botMsg]);
      if (res.actions?.length > 0) applyActions(res.actions);
    } catch (err) {
      const errMsg = err?.response?.data?.message || 'An error occurred. Please check your GROQ_API_KEY.';
      setMessages((prev) => [...prev, { role: 'assistant', content: `❌ ${errMsg}`, actions: [] }]);
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [input, loading, messages, applyActions]);

  const sendMessageWithText = useCallback(async (text) => {
    if (!text || loading) return;
    const userMsg = { role: 'user', content: text, actions: [] };
    const updatedMessages = [...messages, userMsg];
    setMessages(updatedMessages);
    setLoading(true);
    try {
      const payload = updatedMessages.map(({ role, content }) => ({ role, content }));
      const res = await postRequest('/api/chat/message', { messages: payload, shopifyProduct });
      const botMsg = { role: 'assistant', content: res.message, actions: res.actions || [] };
      setMessages((prev) => [...prev, botMsg]);
      if (res.actions?.length > 0) applyActions(res.actions);
    } catch (err) {
      const errMsg = err?.response?.data?.message || 'An error occurred.';
      setMessages((prev) => [...prev, { role: 'assistant', content: `❌ ${errMsg}`, actions: [] }]);
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, messages, applyActions, shopifyProduct]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const resetChat = () => {
    setMessages([WELCOME_MESSAGE]);
    setInput('');
  };

  return (
    <>
      <style>{`
        .panel-message-text { white-space: pre-wrap; word-break: break-word; }
      `}</style>

      {/* Panel — fills parent height via flex column */}
      <div className="flex flex-col h-full bg-white">

        {/* Header */}
        <div className="flex-shrink-0 bg-[#221C51] px-4 py-3 flex items-center gap-3">
          <div className="w-9 h-9 bg-white/10 rounded-full flex items-center justify-center flex-shrink-0">
            <img
              src="/rebot.png"
              alt="AI"
              className="w-6 h-6 object-contain"
              onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
            />
            <span className="text-xl hidden">🤖</span>
          </div>
          <div className="min-w-0">
            <p className="text-white font-bold text-sm leading-tight">OneBotAds AI</p>
            <p className="text-purple-300 text-xs">Ad campaign assistant</p>
          </div>
          <button
            onClick={resetChat}
            title="New conversation"
            className="ml-auto text-purple-300 hover:text-white text-xs px-2.5 py-1.5 rounded-lg hover:bg-white/10 transition border border-white/20 hover:border-white/40"
          >
            New chat
          </button>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4 bg-gray-50">
          {messages.map((msg, i) => (
            <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'} gap-2`}>
              {msg.role === 'assistant' && (
                <div className="w-7 h-7 rounded-full bg-[#221C51] flex items-center justify-center text-sm flex-shrink-0 mt-0.5">
                  🤖
                </div>
              )}
              <div className="flex flex-col gap-1.5 max-w-[82%]">
                <div className={`px-3.5 py-2.5 rounded-2xl text-sm leading-relaxed ${
                  msg.role === 'user'
                    ? 'bg-[#221C51] text-white rounded-br-sm'
                    : 'bg-white text-[#374151] border border-gray-200 rounded-bl-sm shadow-sm'
                }`}>
                  <span className="panel-message-text">{msg.content}</span>
                  {msg.generating && (
                    <span className="inline-block ml-2 align-middle">
                      <svg className="animate-spin w-3.5 h-3.5 text-[#221C51]" viewBox="0 0 24 24" fill="none">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/>
                      </svg>
                    </span>
                  )}
                </div>
                {msg.generatedUrl && (
                  <img
                    src={msg.generatedUrl}
                    alt="Generated creative"
                    className="mt-1.5 rounded-xl border border-gray-200 max-w-full shadow-sm"
                    style={{ maxHeight: '160px', objectFit: 'cover' }}
                  />
                )}
                {msg.mcpTool && msg.mcpData !== undefined && (
                  <McpResultCard tool={msg.mcpTool} data={msg.mcpData} />
                )}
                {msg.libraryAssets && msg.libraryAssets.length > 0 && (
                  <LibraryPickerCard assets={msg.libraryAssets} onPick={handlePickLibraryAsset} />
                )}
                {msg.actions && msg.actions.length > 0 && (
                  <div className="flex flex-wrap gap-1">
                    {msg.actions
                      .filter((a) => !['_submit', '_generate_image', '_generate_video', '_mcp_call', '_library_list', '_library_pick'].includes(a.field))
                      .map((action, j) => (
                        <span key={j} className="inline-flex items-center gap-1 text-xs bg-green-50 text-green-700 border border-green-200 rounded-full px-2 py-0.5 font-medium">
                          <span className="text-green-500">✓</span>
                          {FIELD_LABELS[action.field] || action.field}
                          {action.value !== undefined && (
                            <span className="text-green-600 font-semibold">
                              : {formatActionValue(action.field, action.value)}
                            </span>
                          )}
                        </span>
                      ))}
                  </div>
                )}
              </div>
            </div>
          ))}

          {/* Quick chips — welcome screen only */}
          {messages.length === 1 && !loading && (
            <div className="flex flex-col gap-2 pl-9">
              {QUICK_CHIPS.map((chip) => (
                <button
                  key={chip.label}
                  onClick={() => sendMessageWithText(chip.text)}
                  className="text-left text-xs px-3 py-2 rounded-xl border border-[#221C51]/20 text-[#221C51] hover:bg-[#221C51]/5 transition-colors font-medium"
                >
                  {chip.label}
                </button>
              ))}
            </div>
          )}

          {/* Typing indicator */}
          {loading && (
            <div className="flex justify-start gap-2">
              <div className="w-7 h-7 rounded-full bg-[#221C51] flex items-center justify-center text-sm flex-shrink-0">
                🤖
              </div>
              <div className="bg-white border border-gray-200 rounded-2xl rounded-bl-sm shadow-sm px-4 py-3">
                <TypingDots />
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        {/* Input bar */}
        <div className="flex-shrink-0 px-3 py-3 border-t border-gray-200 bg-white">
          <div className="flex gap-2 items-end">
            <textarea
              ref={inputRef}
              rows={1}
              value={input}
              onChange={(e) => setInput(e.target.value.slice(0, MAX_CHAT_LENGTH))}
              onKeyDown={handleKeyDown}
              placeholder="Describe your campaign..."
              disabled={loading}
              maxLength={MAX_CHAT_LENGTH}
              className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-xl bg-gray-50 focus:outline-none focus:border-[#221C51] resize-none disabled:opacity-50 transition"
              style={{ minHeight: '38px', maxHeight: '100px' }}
              onInput={(e) => {
                e.target.style.height = 'auto';
                e.target.style.height = `${Math.min(e.target.scrollHeight, 100)}px`;
              }}
            />
            <button
              onClick={sendMessage}
              disabled={loading || !input.trim()}
              className="flex-shrink-0 w-10 h-10 bg-[#221C51] text-white rounded-xl flex items-center justify-center hover:bg-[#2d267a] disabled:opacity-40 disabled:cursor-not-allowed transition"
            >
              <svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4">
                <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" />
              </svg>
            </button>
          </div>
          <div className="flex items-center justify-between mt-1.5">
            <p className="text-xs text-gray-400">Enter to send · Shift+Enter for new line</p>
            <span className="text-[10px] text-gray-400 flex-shrink-0 ml-2">{input.length}/{MAX_CHAT_LENGTH}</span>
          </div>
        </div>
      </div>

      {/* Submit confirmation modal */}
      {confirmSubmit && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-2xl shadow-2xl p-6 w-80 flex flex-col gap-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-[#221C51] rounded-full flex items-center justify-center text-xl flex-shrink-0">🚀</div>
              <div>
                <p className="font-bold text-[#221C51] text-sm leading-tight">Launch campaign?</p>
                <p className="text-gray-500 text-xs mt-0.5">The form will be submitted with the current data.</p>
              </div>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => {
                  setConfirmSubmit(false);
                  requestSubmit();
                  setMessages((prev) => [...prev, {
                    role: 'assistant',
                    content: '🚀 Launching… Check the form for the result.',
                    actions: [],
                  }]);
                }}
                className="flex-1 bg-[#221C51] text-white text-sm font-semibold py-2 rounded-xl hover:bg-[#2d267a] transition"
              >
                Confirm
              </button>
              <button
                onClick={() => setConfirmSubmit(false)}
                className="flex-1 bg-gray-100 text-gray-600 text-sm font-semibold py-2 rounded-xl hover:bg-gray-200 transition"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AiChatPanel;
